import React from 'react';
import { Box, Typography, Select, MenuItem, IconButton, Button } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';

interface PaginationFooterProps {
  page: number;
  totalPages: number;
  total: number;
  rowsPerPage: number;
  rowsPerPageOptions?: number[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  label: string;
}

/** Page numbers shown: first 3, last 3, and the current page's immediate neighbors —
 *  gaps between those groups collapse into a single "…". */
function computePageNums(totalPages: number, page: number): number[] {
  const nums: number[] = [];
  const add = (p: number) => { if (p >= 0 && p < totalPages && !nums.includes(p)) nums.push(p); };
  add(0); add(1); add(2);
  add(page - 1); add(page); add(page + 1);
  add(totalPages - 3); add(totalPages - 2); add(totalPages - 1);
  return nums.sort((a, b) => a - b);
}

export function PaginationFooter({
  page, totalPages, total, rowsPerPage, rowsPerPageOptions = [50, 100],
  onPageChange, onRowsPerPageChange, label,
}: PaginationFooterProps) {
  if (total === 0) return null;
  const start = page * rowsPerPage;
  const end = Math.min(start + rowsPerPage, total);
  const pageNums = computePageNums(totalPages, page);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 0.25, pb: 0.1, borderTop: '1px solid #e5e7eb', bgcolor: '#f9fafb', flexShrink: 0 }}>
      <Typography sx={{ fontSize: 11, lineHeight: 1.4, color: '#6b7280', whiteSpace: 'nowrap' }}>Rows per page:</Typography>
      <Select size="small" value={rowsPerPage}
        onChange={e => { onRowsPerPageChange(Number(e.target.value)); onPageChange(0); }}
        sx={{ fontSize: 11, height: 19, minWidth: 52, '.MuiSelect-select': { py: '1px !important', pr: '22px !important', lineHeight: 1.4 } }}>
        {rowsPerPageOptions.map(o => (
          <MenuItem key={o} value={o} sx={{ fontSize: 11 }}>{o}</MenuItem>
        ))}
      </Select>

      <Typography sx={{ fontSize: 11, lineHeight: 1.4, color: '#6b7280', whiteSpace: 'nowrap' }}>
        {start + 1}–{end} of {total} {label}
      </Typography>

      <Box sx={{ flex: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
        <IconButton size="small" disabled={page === 0} onClick={() => onPageChange(page - 1)} sx={{ p: 0.15 }}>
          <ChevronLeftIcon sx={{ fontSize: 14 }} />
        </IconButton>
        {pageNums.map((p, i) => (
          <React.Fragment key={p}>
            {i > 0 && pageNums[i] - pageNums[i - 1] > 1 && (
              <Typography sx={{ fontSize: 11, lineHeight: 1.4, color: '#9ca3af', mx: 0.2 }}>…</Typography>
            )}
            <Button size="small" onClick={() => onPageChange(p)}
              sx={{ minWidth: 19, height: 19, fontSize: 11, lineHeight: 1.4, p: 0,
                bgcolor: p === page ? '#152346' : 'transparent',
                color: p === page ? '#fff' : '#374151',
                '&:hover': { bgcolor: p === page ? '#152346' : '#e5e7eb' } }}>
              {p + 1}
            </Button>
          </React.Fragment>
        ))}
        <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)} sx={{ p: 0.15 }}>
          <ChevronRightIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
